import type { FC, ReactNode } from 'react'
import type { Offer } from '@/types/content'

import { cn } from '@/lib/utils'
import { cms, hasValue, isPlaceholder } from '@/lib/cms'

type SpecColumn = 'betreuungsform' | 'laufzeit' | 'preis' | 'zielgruppe' | 'ablauf'

interface OfferSpecsProps {
    offer: Offer
    columns?: Array<SpecColumn>
    tone?: 'light' | 'dark'
    className?: string
}

export const SPEC_LABELS: Record<SpecColumn, string> = {
    betreuungsform: 'Betreuungsform',
    laufzeit: 'Laufzeit',
    preis: 'Preis',
    zielgruppe: 'Für wen',
    ablauf: 'Ablauf'
}

// Compact facts of an offer as a definition list. Values in square brackets are marked as still to be supplied.
export const OfferSpecs: FC<OfferSpecsProps> = ({
    offer,
    columns = ['betreuungsform', 'laufzeit', 'preis'],
    tone = 'light',
    className
}): ReactNode => {
    const rows = columns.filter(column => hasValue(offer[column]))

    if (rows.length === 0) return null

    return (
        <dl className={cn('flex flex-col', className)}>
            {rows.map(column => {
                const value = offer[column] as string

                return (
                    <div
                        key={column}
                        className={cn('flex flex-col gap-1 border-t py-3 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6', tone === 'dark' ? 'border-ink-line' : 'border-ink/12')}
                    >
                        <dt className={cn('type-label shrink-0', tone === 'dark' ? 'text-ash' : 'text-smoke')}>
                            {SPEC_LABELS[column]}
                        </dt>

                        <dd
                            data-mdlfy-cms={cms('angebote', offer.id, column)}
                            className={cn('text-[0.9375rem] font-medium sm:text-right', isPlaceholder(value) && 'is-placeholder')}
                        >
                            {value}
                        </dd>
                    </div>
                )
            })}
        </dl>
    )
}
